import { fonts } from "@/constants/fonts";
import { useThemeColor } from "@/hooks/useThemeColor";
import { Ionicons } from "@expo/vector-icons";
import React, { useState } from "react";
import {
  FlatList,
  StyleSheet,
  TextInput as RNTextInput,
  type TextInputProps as RNTextInputProps,
  type TextStyle,
  TouchableOpacity,
  View,
  type ViewStyle,
} from "react-native";
import ThemedText from "../core/ThemedText";

export interface AutocompleteOption {
  label: string;
  value: string;
  icon?: React.ReactNode;
  description?: string;
}

interface AutocompleteInputProps extends Omit<RNTextInputProps, "style"> {
  label?: string;
  error?: string;
  options: AutocompleteOption[];
  onSelectOption?: (option: AutocompleteOption) => void;
  variant?: "default" | "filled" | "outlined";
  containerStyle?: ViewStyle;
  inputStyle?: TextStyle;
  disabled?: boolean;
  helperText?: string;
  maxSuggestions?: number;
  filterOptions?: (
    options: AutocompleteOption[],
    query: string
  ) => AutocompleteOption[];
}

export const AutocompleteInput: React.FC<AutocompleteInputProps> = ({
  label,
  error,
  options,
  onSelectOption,
  variant = "default",
  containerStyle,
  inputStyle,
  disabled = false,
  helperText,
  maxSuggestions = 5,
  filterOptions,
  ...props
}) => {
  const backgroundColor = useThemeColor({}, "backgroundTertiary");
  const textColor = useThemeColor({}, "text");
  const borderColor = useThemeColor({}, "border");
  const placeholderColor = useThemeColor({}, "textTertiary");
  const errorColor = useThemeColor({}, "error");
  const disabledTextColor = useThemeColor({}, "textDisabled");
  const disabledBackgroundColor = useThemeColor({}, "backgroundDisabled");
  const suggestionBackground = useThemeColor({}, "background");

  const [showSuggestions, setShowSuggestions] = useState(false);
  const [query, setQuery] = useState(props.value?.toString() || "");

  const defaultFilterOptions = (
    opts: AutocompleteOption[],
    searchQuery: string
  ): AutocompleteOption[] => {
    const lowerQuery = searchQuery.toLowerCase().trim();
    if (!lowerQuery) return [];

    return opts
      .filter(
        (option) =>
          option.label.toLowerCase().includes(lowerQuery) ||
          option.value.toLowerCase().includes(lowerQuery) ||
          option.description?.toLowerCase().includes(lowerQuery)
      )
      .slice(0, maxSuggestions);
  };

  const filteredOptions = filterOptions
    ? filterOptions(options, query)
    : defaultFilterOptions(options, query);

  const handleChangeText = (text: string) => {
    setQuery(text);
    setShowSuggestions(text.length > 0);
    props.onChangeText?.(text);
  };

  const handleSelectOption = (option: AutocompleteOption) => {
    setQuery(option.label);
    setShowSuggestions(false);
    onSelectOption?.(option);
    props.onChangeText?.(option.label);
  };

  const handleClear = () => {
    setQuery("");
    setShowSuggestions(false);
    props.onChangeText?.("");
  };

  const getVariantStyle = (): ViewStyle => {
    const baseStyle: ViewStyle = {
      borderRadius: 12,
      position: "relative",
      backgroundColor,
    };

    switch (variant) {
      case "filled":
        return {
          ...baseStyle,
          backgroundColor: disabled ? disabledBackgroundColor : backgroundColor,
        };
      case "outlined":
        return {
          ...baseStyle,
          backgroundColor: "transparent",
          borderWidth: 1,
          borderColor: error ? errorColor : borderColor,
        };
      default:
        return baseStyle;
    }
  };

  return (
    <View style={[styles.container, containerStyle]}>
      {label && (
        <ThemedText
          style={[styles.label, disabled && { color: disabledTextColor }]}
        >
          {label}
        </ThemedText>
      )}

      <View style={[getVariantStyle(), disabled && styles.disabled]}>
        <RNTextInput
          {...props}
          style={[
            styles.input,
            {
              color: disabled ? disabledTextColor : textColor,
              paddingRight: query.length > 0 ? 40 : 16,
            },
            inputStyle,
          ]}
          value={query}
          onChangeText={handleChangeText}
          onFocus={() => query.length > 0 && setShowSuggestions(true)}
          placeholderTextColor={placeholderColor}
          editable={!disabled}
          autoCorrect={false}
        />

        {query.length > 0 && !disabled && (
          <TouchableOpacity onPress={handleClear} style={styles.clearButton}>
            <Ionicons name="close-circle" size={20} color={placeholderColor} />
          </TouchableOpacity>
        )}
      </View>

      {error && (
        <ThemedText style={[styles.error, { color: errorColor }]}>
          {error}
        </ThemedText>
      )}

      {helperText && !error && (
        <ThemedText style={[styles.helperText, { color: placeholderColor }]}>
          {helperText}
        </ThemedText>
      )}

      {/* Suggestions List */}
      {showSuggestions && filteredOptions.length > 0 && (
        <View
          style={[
            styles.suggestionsContainer,
            {
              backgroundColor: suggestionBackground,
              borderColor,
            },
          ]}
        >
          <FlatList
            data={filteredOptions}
            keyExtractor={(item) => item.value}
            scrollEnabled={filteredOptions.length > 4}
            keyboardShouldPersistTaps="handled"
            renderItem={({ item }) => (
              <TouchableOpacity
                style={[styles.suggestionItem, { borderBottomColor: borderColor }]}
                onPress={() => handleSelectOption(item)}
              >
                {item.icon && (
                  <View style={styles.suggestionIcon}>{item.icon}</View>
                )}
                <View style={styles.suggestionTextContainer}>
                  <ThemedText style={styles.suggestionLabel}>
                    {item.label}
                  </ThemedText>
                  {item.description && (
                    <ThemedText
                      style={[
                        styles.suggestionDescription,
                        { color: placeholderColor },
                      ]}
                    >
                      {item.description}
                    </ThemedText>
                  )}
                </View>
                <Ionicons
                  name="arrow-forward"
                  size={16}
                  color={placeholderColor}
                />
              </TouchableOpacity>
            )}
          />
        </View>
      )}

      {showSuggestions && query.length > 0 && filteredOptions.length === 0 && (
        <View
          style={[
            styles.suggestionsContainer,
            {
              backgroundColor: suggestionBackground,
              borderColor,
            },
          ]}
        >
          <View style={styles.noResults}>
            <Ionicons name="search-outline" size={32} color={placeholderColor} />
            <ThemedText style={[styles.noResultsText, { color: placeholderColor }]}>
              No suggestions found
            </ThemedText>
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 16,
    zIndex: 1,
  },
  label: {
    marginBottom: 4,
    fontSize: 14,
    fontFamily: fonts.medium,
  },
  input: {
    height: 50,
    fontSize: 16,
    padding: 14,
    fontFamily: fonts.regular,
  },
  disabled: {
    opacity: 0.5,
  },
  clearButton: {
    position: "absolute",
    right: 14,
    top: 15,
  },
  error: {
    marginTop: 4,
    fontSize: 12,
    fontFamily: fonts.regular,
  },
  helperText: {
    marginTop: 4,
    fontSize: 12,
    fontFamily: fonts.regular,
  },
  suggestionsContainer: {
    marginTop: 4,
    borderRadius: 12,
    borderWidth: 1,
    maxHeight: 250,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  suggestionItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderBottomWidth: 1,
  },
  suggestionIcon: {
    marginRight: 12,
  },
  suggestionTextContainer: {
    flex: 1,
  },
  suggestionLabel: {
    fontSize: 16,
    fontFamily: fonts.medium,
  },
  suggestionDescription: {
    fontSize: 12,
    marginTop: 2,
    fontFamily: fonts.regular,
  },
  noResults: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 32,
  },
  noResultsText: {
    marginTop: 8,
    fontSize: 14,
    fontFamily: fonts.regular,
  },
});

export default AutocompleteInput;
